import type { PieceData, WhatIf } from '../types';
import { applyAlterations, AlterationOp } from '../../music/alterations';
import { parseKey } from '../../music/key';
import { buildHarmonyEvents } from '../../music/piece';
import { regionsOf } from '../chorales/derive';

/**
 * Hand-written additions to rule-derived sonata pages: minimal pairs chosen by an editor.
 * Each is a small, reviewable change to the notes; the rest of the page stays derived.
 */

function whatIfWithRegions(piece: PieceData, w: Omit<WhatIf, 'regions'> & { ops: AlterationOp[] }): WhatIf {
  const core = applyAlterations({ homeKey: piece.homeKey, measures: piece.measures, notes: piece.notes, harmony: piece.harmony, pedals: piece.pedals }, w.ops);
  const regions = regionsOf(buildHarmonyEvents(core), core.measures, parseKey(piece.homeKey));
  return { ...w, regions };
}

export const SONATA_EDITORIAL: Record<string, (piece: PieceData) => PieceData> = {
  // K. 545, first movement: the recapitulation begins in F major, the subdominant.
  'k545-1': (piece) => {
    const moments = piece.moments.map((m) => {
      if (!/^Coming home/.test(m.feeling)) return m;
      return {
        ...m,
        whatIfs: [
          whatIfWithRegions(piece, {
            id: 'front-door',
            label: 'Come home through the front door',
            question: 'What if the return began in C major, the home key?',
            effect:
              'The opening comes back in C instead of F: it sounds like an arrival rather than a side entrance. Mozart keeps the true homecoming for the second theme. (Bars 42–49 moved down a fourth; bar 50 onward is unchanged, so the seam is audible.)',
            ops: [{ kind: 'transpose', from: 42, to: 49, steps: -3, semitones: -5 }],
          }),
        ],
      };
    });
    return { ...piece, moments };
  },
};
